import { styled } from 'styled-components';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Styling } from '@/styling';
import { useWindowSize } from '@/hooks/useWindowSize';
import { flexGap } from '@/flexGap';

const Container = styled.div``;

const TabList = styled.div`
  position: relative;
  display: flex;
  ${({ theme }) => flexGap(theme.spacing[2])};
  @media (max-width: ${Styling.breakpoint('md')}px) {
    ${({ theme }) => flexGap(theme.spacing[0])};
  }
`;

const TabItem = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  align-items: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  padding: ${Styling.spacing(1, 2)};
  color: ${({ theme }) => theme.palette.secondary.main};
  cursor: pointer;
  white-space: nowrap;
  box-sizing: content-box;
  background-color: inherit;
  border: 0;

  &:hover,
  &.active {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &:disabled {
    color: ${({ theme }) => theme.palette.grayscale.light2};
    cursor: not-allowed;
  }
`;

const TabItemText = styled.div`
  ${Styling.typography('body')};
  font-size: 1.05rem;

  &.active {
    font-weight: bold;
  }
`;

const ActiveIndicator = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.main};
  position: absolute;
  bottom: 0;
  left: 0;
  height: 1px;
  width: 0;
  transition:
    left 100ms,
    width 100ms;
`;

export type Tab = {
  name: string;
  icon?: ReactNode;
  disabled?: boolean;
};

export type TabsProps = {
  label?: string;
  active: string;
  tabs: Tab[];
  onChange?(tab: string): void;
};

export const Tabs: FC<TabsProps> = ({
  label = 'tabs',
  active,
  tabs,
  onChange,
}) => {
  const [activeTabElement, setActiveTabElement] =
    useState<HTMLButtonElement | null>(null);
  const activeIndicatorRef = useRef<HTMLDivElement | null>(null);
  const windowSize = useWindowSize();

  useEffect(() => {
    if (activeTabElement && activeIndicatorRef.current) {
      activeIndicatorRef.current.style.width = `${activeTabElement.offsetWidth}px`;
      activeIndicatorRef.current.style.left = `${activeTabElement.offsetLeft}px`;
    }
  }, [activeTabElement, windowSize]);

  const handleClick = (tab: Tab) => onChange?.(tab.name);

  return (
    <Container>
      <TabList role="tablist" aria-label={label}>
        {tabs.map((tab) => (
          <TabItem
            role="tab"
            tabIndex={0}
            aria-label={tab.name}
            aria-selected={tab.name === active}
            key={tab.name}
            disabled={tab.disabled}
            // Callback ref to trigger re-render on change
            ref={
              tab.name === active ? (el) => setActiveTabElement(el) : undefined
            }
            className={tab.name === active ? 'active' : ''}
            onClick={() => handleClick(tab)}
          >
            {tab.icon}
            <TabItemText className={tab.name === active ? 'active' : ''}>
              {tab.name}
            </TabItemText>
          </TabItem>
        ))}
        <ActiveIndicator ref={activeIndicatorRef} />
      </TabList>
    </Container>
  );
};
